import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Settings, Flag, AlertTriangle, Pause, Play, Trash2, Plus, Server, Flame } from "lucide-react";

export default function AdminExploitation() {
  const [tab, setTab] = useState("flags");
  return (
    <div className="space-y-6" data-testid="admin-exploitation-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Lot Exploitation</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><Settings className="w-7 h-7 text-emerald-400" /> Exploitation</h1>
        <p className="text-sm text-slate-400 mt-1">Feature flags, bannière de maintenance, contrôle des jobs de fond.</p>
      </div>
      <div className="inline-flex bg-[#0f172a] border border-slate-800 rounded-md p-1">
        {[["flags", "Feature flags", Flag], ["maintenance", "Maintenance", AlertTriangle], ["jobs", "Jobs", Server], ["danger", "Danger zone", Flame]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ops-tab-${k}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs ${tab === k ? "bg-[#1F5A99] text-white" : "text-slate-400 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "flags" && <Flags />}
      {tab === "maintenance" && <Maintenance />}
      {tab === "jobs" && <Jobs />}
      {tab === "danger" && <DangerZone />}
    </div>
  );
}

function DangerZone() {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [subPwd, setSubPwd] = useState("");
  const [subConfirm, setSubConfirm] = useState("");
  const [subRunning, setSubRunning] = useState(false);
  const [subResult, setSubResult] = useState(null);
  const run = async () => {
    if (confirm !== "PURGE_TESTS_CONFIRM") return toast.error("Tapez exactement PURGE_TESTS_CONFIRM");
    if (!pwd) return toast.error("Mot de passe admin requis");
    if (!window.confirm("Cette action supprime scans, factures, transactions et logs. Continuer ?")) return;
    setRunning(true);
    try {
      const { data } = await adminApi.post("/dangerous/purge-tests", { admin_password: pwd, confirm });
      setResult(data);
      setPwd(""); setConfirm("");
      toast.success(`${data.total_deleted} document(s) supprimé(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRunning(false); }
  };
  const runSubCleanup = async (dryRun) => {
    if (subConfirm !== "CLEANUP_SUB_ORDERS_CONFIRM") return toast.error("Tapez exactement CLEANUP_SUB_ORDERS_CONFIRM");
    if (!subPwd) return toast.error("Mot de passe admin requis");
    setSubRunning(true);
    try {
      const { data } = await adminApi.post("/dangerous/cleanup-subscription-orders", {
        admin_password: subPwd, confirm: subConfirm, dry_run: !!dryRun,
      });
      setSubResult(data);
      if (!dryRun) { setSubPwd(""); setSubConfirm(""); }
      toast.success(dryRun ? `${data.would_delete} facture(s) 0€ à purger (simulation)` : `${data.deleted} facture(s) 0€ supprimée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSubRunning(false); }
  };
  return (
    <div className="space-y-4" data-testid="danger-zone">
      <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-rose-300 flex items-center gap-2 mb-3">
          <Flame className="w-4 h-4" /> Purge tests — remise à zéro du CA
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed mb-4">
          Supprime définitivement toutes les données de test : scans, allégations, commandes, factures,
          transactions Stripe, alertes, journaux, audits, files de recrawl, métriques.<br/>
          <span className="text-emerald-300">Conservé</span> : utilisateurs, sites, preuves, coupons, configuration, CGV.
        </p>
        <div className="space-y-3">
          <input type="password" placeholder="Mot de passe admin" value={pwd} onChange={e => setPwd(e.target.value)}
            data-testid="danger-pwd" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <input type="text" placeholder="Tapez PURGE_TESTS_CONFIRM" value={confirm} onChange={e => setConfirm(e.target.value)}
            data-testid="danger-confirm" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
          <button onClick={run} disabled={running || confirm !== "PURGE_TESTS_CONFIRM"} data-testid="danger-run"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-xs">
            <Trash2 className="w-3.5 h-3.5" /> {running ? "Purge en cours…" : "Exécuter la purge"}
          </button>
        </div>
      </div>
      {result && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5" data-testid="danger-result">
          <p className="text-xs text-emerald-300 mb-3">✓ Purge réussie : {result.total_deleted} document(s) — quotas remis à zéro pour {result.users_quota_reset} utilisateur(s)</p>
          <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
            {Object.entries(result.by_collection || {}).map(([c, n]) => (
              <div key={c} className="text-slate-400">{c}: <span className="text-white">{n}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-6" data-testid="cleanup-sub-orders">
        <h3 className="text-sm font-semibold text-amber-300 flex items-center gap-2 mb-3">
          <Flame className="w-4 h-4" /> Rétro-nettoyage — factures scan 0 € des abonnés
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed mb-4">
          Supprime les commandes <span className="font-mono">type=scan</span> à <b>0 €</b> sans référence Stripe, générées à tort avant le fix du bug de facturation.
          Ces lignes n'ont aucune valeur comptable et cassent le funnel (comptage <span className="font-mono">paid_scans</span>, taux de conversion, revenus par produit).<br/>
          <span className="text-emerald-300">Rien d'autre n'est touché</span> — pas d'impact sur les utilisateurs, abonnements ni scans eux-mêmes.
        </p>
        <div className="space-y-3">
          <input type="password" placeholder="Mot de passe admin" value={subPwd} onChange={e => setSubPwd(e.target.value)}
            data-testid="cleanup-sub-pwd" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <input type="text" placeholder="Tapez CLEANUP_SUB_ORDERS_CONFIRM" value={subConfirm} onChange={e => setSubConfirm(e.target.value)}
            data-testid="cleanup-sub-confirm" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
          <div className="flex gap-2">
            <button onClick={() => runSubCleanup(true)} disabled={subRunning || subConfirm !== "CLEANUP_SUB_ORDERS_CONFIRM"} data-testid="cleanup-sub-dry"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white text-xs">
              Simulation (dry-run)
            </button>
            <button onClick={() => runSubCleanup(false)} disabled={subRunning || subConfirm !== "CLEANUP_SUB_ORDERS_CONFIRM"} data-testid="cleanup-sub-run"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-xs">
              <Trash2 className="w-3.5 h-3.5" /> {subRunning ? "Nettoyage…" : "Exécuter le nettoyage"}
            </button>
          </div>
        </div>
      </div>
      {subResult && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5" data-testid="cleanup-sub-result">
          <p className="text-xs text-emerald-300 mb-3">
            {subResult.dry_run ? `Simulation : ${subResult.would_delete} facture(s) 0€ éligibles.` : `✓ ${subResult.deleted} facture(s) 0€ supprimée(s).`}
          </p>
          {subResult.sample && subResult.sample.length > 0 && (
            <div className="text-[11px] font-mono text-slate-400 space-y-1">
              {subResult.sample.slice(0, 5).map((o) => (
                <div key={o.invoice_number || o.id}>{o.invoice_number} · {(o.label || '').slice(0, 60)}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Flags() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ key: "", description: "", enabled: false, scope: "global", user_id: "" });
  const load = async () => {
    try { const { data } = await adminApi.get("/flags"); setRows(data); }
    catch (e) { console.error("Flags load failed:", e); toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await adminApi.post("/flags", { ...form, user_id: form.scope === "client" ? form.user_id : null });
      toast.success("Flag créé"); setShowNew(false); setForm({ key: "", description: "", enabled: false, scope: "global", user_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (f) => {
    try { await adminApi.post(`/flags/${f.id}/toggle`, { enabled: !f.enabled }); load(); }
    catch { toast.error("Erreur"); }
  };
  const del = async (f) => {
    if (!window.confirm(`Supprimer le flag ${f.key} ?`)) return;
    try { await adminApi.delete(`/flags/${f.id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <p className="text-xs text-slate-400">Activez/désactivez des fonctionnalités globalement ou par client. Un flag client override le global.</p>
        <button onClick={() => setShowNew(!showNew)} data-testid="flag-new-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs"><Plus className="w-3.5 h-3.5" /> Nouveau flag</button>
      </div>
      {showNew && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 space-y-3" data-testid="flag-form">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="clé (ex: beta_playwright)" value={form.key} onChange={e => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              data-testid="flag-key" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
            <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} data-testid="flag-scope"
              className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
              <option value="global">Global (tous les clients)</option>
              <option value="client">Client spécifique</option>
            </select>
          </div>
          <input placeholder="Description (optionnel)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          {form.scope === "client" && (
            <input placeholder="user_id du client cible" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })}
              data-testid="flag-user-id" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
          )}
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} data-testid="flag-enabled" />
            Activé dès la création
          </label>
          <button onClick={create} data-testid="flag-create-btn" className="px-4 py-2 rounded-md bg-emerald-600 text-white text-xs">Créer</button>
        </div>
      )}
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <table className="w-full text-xs">
          <thead><tr className="uppercase text-[10px] text-slate-500 text-left"><th className="p-2">Clé</th><th className="p-2">Scope</th><th className="p-2">Client</th><th className="p-2">Description</th><th className="p-2">État</th><th className="p-2">Créé par</th><th className="p-2"></th></tr></thead>
          <tbody>{rows.map(f => (
            <tr key={f.id} className="border-t border-slate-800" data-testid={`flag-row-${f.key}`}>
              <td className="p-2 font-mono text-emerald-400">{f.key}</td>
              <td className="p-2"><span className={`px-2 py-0.5 rounded text-[10px] uppercase font-mono ${f.scope === "global" ? "bg-blue-500/20 text-blue-300" : "bg-amber-500/20 text-amber-300"}`}>{f.scope}</span></td>
              <td className="p-2 text-slate-400">{f.user_email || "—"}</td>
              <td className="p-2 text-slate-400 max-w-xs truncate">{f.description || "—"}</td>
              <td className="p-2">
                <button onClick={() => toggle(f)} data-testid={`flag-toggle-${f.key}`}
                  className={`px-3 py-1 rounded text-[10px] font-mono uppercase ${f.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                  {f.enabled ? "ON" : "OFF"}
                </button>
              </td>
              <td className="p-2 text-slate-500 text-[10px]">{f.created_by}</td>
              <td className="p-2"><button onClick={() => del(f)} data-testid={`flag-delete-${f.key}`} className="text-rose-400 hover:underline"><Trash2 className="w-3 h-3" /></button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Aucun flag</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Maintenance() {
  const [form, setForm] = useState({ enabled: false, message: "", level: "info", dismissible: true, scheduled_at: "" });
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const { data } = await adminApi.get("/maintenance"); setForm({ ...form, ...data }); }
    catch (e) { console.error("Maintenance load failed:", e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at || null };
      await adminApi.put("/maintenance", payload);
      toast.success(form.enabled ? "Bannière activée" : "Bannière désactivée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const levelColors = {
    info: "bg-blue-500/10 border-blue-500/30 text-blue-300",
    warning: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    critical: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  };
  return (
    <div className="space-y-4">
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6" data-testid="maintenance-form">
        <h3 className="text-sm font-semibold mb-4">Bannière de maintenance</h3>
        <label className="flex items-center gap-3 mb-4">
          <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} data-testid="maintenance-enabled" />
          <span className="text-sm">Afficher la bannière sur toutes les pages publiques et /app</span>
        </label>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <select value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} data-testid="maintenance-level"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="info">Info (bleu)</option>
            <option value="warning">Warning (orange)</option>
            <option value="critical">Critical (rouge)</option>
          </select>
          <input type="datetime-local" value={form.scheduled_at?.slice(0, 16) || ""} onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
            placeholder="Date planifiée (optionnel)" data-testid="maintenance-scheduled"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
        </div>
        <textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
          placeholder="Message (400 caractères max)" maxLength={400} data-testid="maintenance-message"
          className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs mb-3" />
        <label className="flex items-center gap-2 text-xs mb-4">
          <input type="checkbox" checked={form.dismissible} onChange={e => setForm({ ...form, dismissible: e.target.checked })} data-testid="maintenance-dismissible" />
          <span>Utilisateur peut fermer la bannière (X)</span>
        </label>
        <button onClick={save} disabled={saving} data-testid="maintenance-save"
          className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
      {form.message && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Aperçu</p>
          <div className={`border rounded-md p-4 text-sm ${levelColors[form.level]}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{form.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Jobs() {
  const [status, setStatus] = useState(null);
  const load = async () => {
    try { const { data } = await adminApi.get("/jobs/status"); setStatus(data); }
    catch (e) { console.error("Jobs status failed:", e); toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async (key) => {
    try { await adminApi.post("/jobs/toggle", { [key]: !status[key] }); toast.success("État mis à jour"); load(); }
    catch { toast.error("Erreur"); }
  };
  const purge = async (target) => {
    const conf = window.prompt(`Retapez EXACTEMENT "${target}" pour confirmer la purge :`);
    if (conf !== target) return toast.error("Confirmation incorrecte");
    try { const { data } = await adminApi.post("/jobs/purge", { target, confirm: conf });
      toast.success(`${data.deleted} élément(s) supprimé(s) de ${target}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  if (!status) return <p className="text-slate-500 text-sm">Chargement…</p>;
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <JobCard label="Recrawl (surveillance)" paused={status.recrawl_paused}
          onToggle={() => toggle("recrawl_paused")} testid="job-recrawl" />
        <JobCard label="Billing (dunning + réconciliation)" paused={status.billing_paused}
          onToggle={() => toggle("billing_paused")} testid="job-billing" />
      </div>
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Server className="w-4 h-4" /> Files d'attente & purges</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["recrawl_queue", "File de recrawl", status.queues.recrawl_queue],
            ["failed_payments", "Paiements en échec", status.queues.failed_payments],
            ["expired_otps", "OTP admin consommés", status.queues.expired_otps],
            ["pending_uploads", "Uploads en attente", 0],
          ].map(([target, label, count]) => (
            <div key={target} className="flex items-center justify-between p-3 bg-[#0b1220] rounded-md border border-slate-800" data-testid={`queue-${target}`}>
              <div>
                <p className="text-sm">{label}</p>
                <p className="text-[10px] text-slate-500 font-mono">{count} élément(s)</p>
              </div>
              <button onClick={() => purge(target)} data-testid={`purge-${target}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-rose-600/20 border border-rose-500/40 text-rose-300 text-xs">
                <Trash2 className="w-3 h-3" /> Purger
              </button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-500 mt-3">Toutes les purges sont journalisées dans le journal d'audit.</p>
      </div>
    </div>
  );
}

function JobCard({ label, paused, onToggle, testid }) {
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{label}</p>
      <div className="flex items-center justify-between mt-2">
        <span className={`px-3 py-1 rounded-full text-[10px] uppercase font-mono ${paused ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>
          {paused ? "En pause" : "En cours"}
        </span>
        <button onClick={onToggle} data-testid={`${testid}-toggle`}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${paused ? "bg-emerald-600 text-white" : "bg-amber-600/20 border border-amber-500/40 text-amber-300"}`}>
          {paused ? <><Play className="w-3 h-3" /> Reprendre</> : <><Pause className="w-3 h-3" /> Mettre en pause</>}
        </button>
      </div>
    </div>
  );
}
